/// What somebody picks first: how big the picture should be.
const RESOLUTIONS = [2160, 1080, 720, 480];

/// And what hangs off each of those: how much bandwidth it may take.
const BITRATES: Record<number, number[]> = {
  2160: [40_000_000, 20_000_000, 12_000_000],
  1080: [20_000_000, 10_000_000, 6_000_000, 4_000_000],
  720: [8_000_000, 4_000_000, 2_000_000],
  480: [3_000_000, 1_500_000, 750_000],
};

export interface QualityChoice {
  height: number;
  /// Bandwidth ceilings worth offering at this size, largest first.
  bitrates: number[];
  /// Whether "automatic" belongs under it: only where the size is a real reduction.
  hasAutomatic: boolean;
}

/// The qualities a particular file can actually be delivered at.
///
/// Offering 4K for a 360p source invites an upscale, which costs an encoder and gives back
/// nothing that was not already there. Offering twenty megabits for a file that runs at two is
/// the same mistake in the other direction. So the list is derived from the file: sizes at or
/// below what it holds, and only the bandwidth ceilings that would actually save something.
///
/// A size equal to the source is only worth showing when something can still be saved on the
/// bandwidth - otherwise it is the original under another name, and the original is its own
/// entry already.
export function qualityChoices(sourceHeight?: number, sourceBitrate?: number): QualityChoice[] {
  const choices: QualityChoice[] = [];

  for (const height of RESOLUTIONS) {
    if (sourceHeight && height > sourceHeight) {
      continue;
    }

    const bitrates = (BITRATES[height] ?? []).filter(
      (bitrate) => !sourceBitrate || bitrate < sourceBitrate,
    );

    const smallerThanSource = !sourceHeight || height < sourceHeight;

    if (!smallerThanSource && bitrates.length === 0) {
      continue;
    }

    choices.push({ height, bitrates, hasAutomatic: smallerThanSource });
  }

  return choices;
}

/// The names people actually use for these sizes.
export function resolutionName(height: number): string {
  switch (height) {
    case 2160:
      return '4K';
    case 1080:
      return 'Full HD';
    case 720:
      return 'HD';
    default:
      return 'SD';
  }
}
